import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Auth0ProviderWithNavigate } from '../../auth0-provider';
import { useEpcsActivationWatcher } from '@features/eprescriptions';
import { Toolbar, DEFAULT_TOOLBAR_ACTIONS } from '@features/toolbar';
import { useFeatureFlags } from '@features/user';
import { useSystemNotificationModals } from '@features/system-notifications';
import { useGlobalKeyboardShortcuts } from '../../hooks/useGlobalKeyboardShortcuts';
import { PageViewObserver, useFeatureLinksAccess } from '@features/knowledge-base/links';

// The knowledge base module carries the article renderer; keep it out of the shell chunk
// and do not fetch it at all while feature_guide_links is closed (GDN-3694, AC-7).
const GuideDrawer = lazy(() =>
  import('@features/knowledge-base').then(module => ({ default: module.GuideDrawer })),
);

/**
 * Layout component that handles authentication and wraps
 * authenticated routes
 */
export const AuthenticatedLayout = () => {
  const featureFlags = useFeatureFlags();

  // Trigger system notification modals when they become active
  useSystemNotificationModals();

  // Watch for EPCS activation and show toast on status change
  useEpcsActivationWatcher();

  // ⌘K command palette + future app-wide shortcuts
  useGlobalKeyboardShortcuts();
  // The drawer mounts for the gate minus the holdout's control arm, without
  // counting as an exposure (GDN-3707); GuideLink logs the exposure.
  const { enabled: guideLinksEnabled } = useFeatureLinksAccess({ logExposure: false });

  // Only render toolbar if any of these feature flags are enabled
  const shouldShowToolbar =
    featureFlags.lab_orders ||
    featureFlags.stripe_connect ||
    featureFlags.forms ||
    featureFlags.document_generation;

  return (
    <Auth0ProviderWithNavigate>
      <PageViewObserver />
      <Outlet />
      {shouldShowToolbar && <Toolbar actions={DEFAULT_TOOLBAR_ACTIONS} />}
      {guideLinksEnabled && (
        <Suspense fallback={null}>
          <GuideDrawer />
        </Suspense>
      )}
    </Auth0ProviderWithNavigate>
  );
};
